import * as React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module')
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error, 
      isChunkError: isChunkLoadError(error) 
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  clearAllCaches = async (): Promise<void> => {
    // Clear all Cache Storage
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      } catch (e) {
        console.warn('Failed to clear caches:', e);
      }
    }

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      } catch (e) {
        console.warn('Failed to unregister service workers:', e);
      }
    }

    // Clear chunk refresh flags
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('chunk_refresh_')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  handleRefresh = async () => {
    await this.clearAllCaches();
    
    // Force hard reload with cache-busting
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  handleReloadApp = async () => {
    localStorage.removeItem('garage-renaissance-last-route');
    await this.clearAllCaches();
    
    // Force hard reload to home
    const url = new URL(window.location.origin);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-4 md:p-8">
            <div className="max-w-md w-full text-center">
              <h1 className="text-lg md:text-2xl font-bold text-foreground mb-2">New Version Available</h1>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                The application has been updated. Please refresh to load the latest version.
              </p>
              <button 
                onClick={this.handleRefresh}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4 md:p-8">
          <div className="max-w-md w-full text-center">
            <h1 className="text-lg md:text-2xl font-bold text-destructive mb-4">Something went wrong</h1>
            <pre className="mt-4 text-left text-xs md:text-sm bg-muted p-3 md:p-4 rounded overflow-auto max-h-48 text-muted-foreground">
              {this.state.error?.message}
            </pre>
            <button 
              onClick={this.handleReloadApp}
              className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
